"use client";

import { useState } from "react";
import BurgerMenuIcon from "@/public/burger-menu.svg";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import Link from "next/link";
import Image from "next/image";
import { ACADEMY_NAV_ITEM, MAIN_NAV_ITEMS, NAV_ITEMS, type HeaderNavId } from "@/constants/header";
import { SHOW_ACADEMY, SHOW_JOBS } from "@/constants/feature-flags";
import { LocalizedLink } from "../ui/LocalizedLink";
import { LanguagePicker } from "@/components/ui/LanguagePicker";
import type { MainLayoutHeaderQueryResult } from "@/sanity/schemas/lib/queries";

interface SiteHeaderProps {
    text: MainLayoutHeaderQueryResult;
    contactLabel: string;
    lang: string;
}

export function SiteHeader({ text, contactLabel, lang }: SiteHeaderProps) {
    const [mode] = useState<"dark" | "light">("dark");
    const [menuOpen, setMenuOpen] = useState(false);
    const isDark = mode === "dark";
    const navigationLabels: Record<HeaderNavId, string | null> = {
        ...text.navigation,
        contact: contactLabel,
    };
    const hasLabel = (id: HeaderNavId) =>
        (SHOW_JOBS || id !== "jobs") && Boolean(navigationLabels[id]);

    return (
        <header
            className={cn(
                "fixed top-0 left-0 z-50 w-full",
                isDark ? "text-white" : "text-ink",
                menuOpen ? (isDark ? "bg-dark-theme" : "bg-white") : "bg-transparent",
            )}
        >
            <div className="flex h-21 items-center justify-between gap-4 px-5 md:px-10">
                <LocalizedLink lang={lang} href="/" className="flex items-center gap-3">
                    <Image src="/oe-logo.svg" alt={text.logoAlt} width={60} height={60} priority />
                </LocalizedLink>

                <div className="hidden items-center gap-4 md:flex">
                    <div
                        className={cn(
                            "flex items-center gap-3 rounded-xl px-2 py-2 bg-white/10  backdrop-blur-md",
                        )}
                    >
                        {MAIN_NAV_ITEMS.filter((item) => hasLabel(item.id)).map((item) => (
                            <LocalizedLink
                                key={item.id}
                                lang={lang}
                                href={item.href}
                                className={cn(
                                    "cursor-pointer rounded-[10px] px-4 py-1 text-base font-medium transition text-nav-tab-hover hover:text-[#707070]",
                                )}
                            >
                                {navigationLabels[item.id]}
                            </LocalizedLink>
                        ))}
                    </div>
                    {SHOW_ACADEMY && (
                        <Link href={ACADEMY_NAV_ITEM.href}>
                            <Button
                                variant="dark-4"
                                className="text-base bg-white/10  backdrop-blur-md text-nav-tab-hover hover:text-[#707070] hover:bg-white/10"
                            >
                                {text.academyButton}
                            </Button>
                        </Link>
                    )}
                </div>

                <button
                    type="button"
                    className={cn(
                        "cursor-pointer rounded-[10px] bg-transparent p-2 md:hidden",
                        isDark ? "text-header-dark-burger" : "text-header-light-burger",
                    )}
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label={text.menuLabel}
                >
                    <BurgerMenuIcon className="h-5 w-5" />
                </button>
            </div>

            {menuOpen && (
                <div className="fixed inset-0 top-21 z-50 bg-dark-theme md:hidden overflow-y-auto">
                    <div className="flex flex-col px-5 py-6 min-h-full">
                        <nav className="mb-8 flex flex-col gap-4">
                            {(SHOW_ACADEMY ? NAV_ITEMS : MAIN_NAV_ITEMS)
                                .filter((item) => hasLabel(item.id))
                                .map((item) => (
                                    <LocalizedLink
                                        lang={lang}
                                        key={item.id}
                                        href={item.href}
                                        className="cursor-pointer text-left text-base text-white hover:text-white/70 transition"
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        {navigationLabels[item.id]}
                                    </LocalizedLink>
                                ))}
                        </nav>

                        <div className="border-t border-white/10 pt-6">
                            <div className="mb-4 text-xs text-white/50">{text.languageLabel}</div>
                            <LanguagePicker placement="top" />
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
